import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { XMarkIcon } from "@heroicons/react/24/solid";
import {
  getFirestore,
  doc,
  updateDoc,
  arrayUnion,
  getDoc,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase";

export default function FormSubmissionModal({
  confirmTitle,
  confirmSubtitle,
  SGloopGroup,
  benefits,
  onClose,
}) {
  const { user } = useAuth();
  const db = getFirestore(firebaseApp);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);

  // ✅ Check if user has already joined the group
  useEffect(() => {
    const checkIfJoined = async () => {
      if (!user || !SGloopGroup) return;
      try {
        const groupSnap = await getDoc(doc(db, SGloopGroup.path));
        if (groupSnap.exists()) {
          const groupData = groupSnap.data();
          const userRefPath = `users/${user.uid}`;
          const members = groupData.subMembershipSubscription || [];
          console.log(groupData)
          // Check if userRef already in array
          const alreadyJoined = members.some(
            (ref) => ref?.path === userRefPath
          );

          if (alreadyJoined) {
            setJoined(true);
          }
        }
      } catch (err) {
        console.error("Error checking group membership:", err);
      }
    };

    checkIfJoined();
  }, [user, SGloopGroup, db]);

  // ✅ Handle Join Button
  const handleJoinGroup = async () => {
    if (!SGloopGroup) return;
    if (joining || joined) return; // prevent double click

    if (user) {
      try {
        setJoining(true);
        const userRef = doc(db, "users", user.uid);

        await updateDoc(doc(db, SGloopGroup.path), {
          subMembershipSubscription: arrayUnion(userRef),
        });

        setJoined(true);
      } catch (err) {
        console.error("Error joining group:", err);
        alert("Failed to join group. Please try again later.");
      } finally {
        setJoining(false);
      }
    } else {
      // Save redirect and send user to login
      const routeBackUrl = `/SGLoop?id=${SGloopGroup.path.split("/")[1]}&subId=${SGloopGroup.id}`;
      sessionStorage.setItem("routeback", routeBackUrl);
      window.location.href = "/login";
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40">
      <div className="relative bg-white rounded-2xl p-6 sm:p-8 max-w-md w-[90%] text-center shadow-xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition"
          aria-label="Close modal"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        {/* Title & Subtitle */}
        <h2 className="text-2xl font-bold text-gray-800 mb-2">{confirmTitle}</h2>
        <p className="text-gray-600 mb-5">{confirmSubtitle}</p>

        {/* Group Section */}
        {SGloopGroup && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4 text-left">
            <h3 className="font-semibold text-blue-800 mb-1">Join Our Community</h3>
            <p className="text-sm text-gray-700 mb-2">
              Stay connected and receive updates by joining our SGloop group:
            </p>
            <a
              href={`/SGLoop?id=${SGloopGroup.path.split("/")[1]}&subId=${SGloopGroup.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="block text-blue-700 font-medium hover:underline mb-3"
            >
              {SGloopGroup.subGroupName}
            </a>

            <button
              onClick={handleJoinGroup}
              disabled={joining || joined}
              className={`w-full py-2 rounded-lg font-medium transition-colors ${
                joined
                  ? "bg-green-600 text-white cursor-default"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              } disabled:opacity-70`}
            >
              {joining ? "Joining..." : joined ? "Joined!" : "Join Group"}
            </button>
          </div>
        )}

        {/* Benefits Section */}
        {benefits && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
            <h3 className="font-semibold text-gray-800 mb-1">Benefits</h3>
            <p className="text-sm text-gray-700 whitespace-pre-line">{benefits}</p>
          </div>
        )}
      </div>
    </div>
  );
}
